// The problem section: a server, part by part, mapped onto Cloudflare primitives.
// Scroll-scrubbed and reversible on wide screens; stacked rows that scrub one by one on mobile.
import { clamp01, context2d, onFrame, smooth, state, viewportWatcher } from "./core.ts"

type Styled = HTMLElement | SVGElement
type Point = [number, number]

const bez = (p0: Point, p3: Point, t: number): Point => {
  const mx = (p0[0] + p3[0]) / 2
  const p1: Point = [mx, p0[1]]
  const p2: Point = [mx, p3[1]]
  const u = 1 - t
  return [
    u * u * u * p0[0] + 3 * u * u * t * p1[0] + 3 * u * t * t * p2[0] + t * t * t * p3[0],
    u * u * u * p0[1] + 3 * u * u * t * p1[1] + 3 * u * t * t * p2[1] + t * t * t * p3[1],
  ]
}

export function initMapping(): void {
  const sec = document.querySelector<HTMLElement>(".map")
  if (!sec) return
  const stage = sec.querySelector<HTMLElement>(".map-stage")
  const rows = [...sec.querySelectorAll<HTMLElement>(".mrow")]
  // Indexed by data-i, so slab i, its label and its tile always pair up.
  const slabs: Styled[] = []
  const labels: Styled[] = []
  const tiles: Styled[] = []
  if (stage) {
    for (const g of stage.querySelectorAll<SVGElement>(".slab")) slabs[Number(g.dataset.i)] = g
    for (const l of stage.querySelectorAll<Styled>(".slab-l")) labels[Number(l.dataset.i)] = l
    for (const t of stage.querySelectorAll<Styled>(".ptile")) tiles[Number(t.dataset.i)] = t
  }
  const N = tiles.length || rows.length
  const chores = stage ? [...stage.querySelectorAll<HTMLElement>(".chores li")] : []
  const right = stage?.querySelector<HTMLElement>(".map-right")
  const meter = sec.querySelector<HTMLElement>(".pin-meter")
  const svg = stage?.querySelector<SVGSVGElement>(".rack-svg")
  const cv = stage?.querySelector<HTMLCanvasElement>(".map-flow")
  const ctx = cv ? context2d(cv) : undefined
  const VB = svg ? svg.viewBox.baseVal : null
  const famColor = (el: Styled) => getComputedStyle(el).getPropertyValue("--fc").trim()
  let hover = -1
  let lastS = -9
  let colors: string[] = []
  const pair = (i: number, on: boolean) => {
    for (const el of [slabs[i], labels[i], tiles[i]]) el?.classList.toggle("hi", on)
    rows[i]?.classList.toggle("hi", on)
    hover = on ? i : -1
    lastS = -9
  }
  for (const el of [...labels, ...tiles]) {
    if (!el) continue
    const i = Number(el.dataset.i)
    el.addEventListener("pointerenter", () => pair(i, true))
    el.addEventListener("pointerleave", () => pair(i, false))
    el.addEventListener("focus", () => pair(i, true))
    el.addEventListener("blur", () => pair(i, false))
  }
  const anchors = (canvas: HTMLCanvasElement, rack: SVGSVGElement, vb: DOMRect) => {
    const cr = canvas.getBoundingClientRect()
    const sr = rack.getBoundingClientRect()
    const sx = sr.width / vb.width
    const sy = sr.height / vb.height
    return slabs.map((g, i) => {
      const tr = tiles[i]?.getBoundingClientRect()
      const a: Point = [
        sr.left - cr.left + Number(g.dataset.ax) * sx,
        sr.top - cr.top + Number(g.dataset.ay) * sy,
      ]
      const b: Point = tr ? [tr.left - cr.left, tr.top - cr.top + tr.height / 2] : a
      return { a, b }
    })
  }
  const moved = viewportWatcher()
  onFrame(() => {
    // Everything below depends on scroll position, except the hover highlight. With neither changing
    // there is nothing to redraw, and skipping saves a forced layout every frame.
    if (!moved() && hover < 0) return
    const vh = innerHeight
    // Stacked rows (mobile and reduced motion): each row scrubs as it crosses the viewport.
    if (!stage?.offsetParent || state.reduced) {
      for (const r of rows) {
        const rr = r.getBoundingClientRect()
        r.style.setProperty(
          "--k",
          state.reduced ? "1" : smooth(0, 1, (vh * 0.85 - rr.top) / (vh * 0.3)).toFixed(3),
        )
      }
      return
    }
    if (!cv || !ctx || !svg || !VB) return
    const r = sec.getBoundingClientRect()
    const p = clamp01(-r.top / Math.max(1, r.height - vh))
    const s = p * (N + 1.8) - 0.4
    if (Math.abs(s - lastS) < 0.002 && hover < 0) return
    lastS = s
    meter?.style.setProperty("--p", p.toFixed(4))
    if (cv.width !== Math.round(cv.clientWidth * devicePixelRatio)) {
      cv.width = Math.round(cv.clientWidth * devicePixelRatio)
      cv.height = Math.round(cv.clientHeight * devicePixelRatio)
      colors = tiles.map(famColor)
    }
    if (!colors.length) colors = tiles.map(famColor)
    const d = devicePixelRatio
    ctx.setTransform(d, 0, 0, d, 0, 0)
    ctx.clearRect(0, 0, cv.width, cv.height)
    const A = anchors(cv, svg, VB)
    for (let i = 0; i < N; i++) {
      const slab = slabs[i]
      const label = labels[i]
      const tile = tiles[i]
      const anchor = A[i]
      if (!slab || !label || !tile || !anchor) continue
      const k = clamp01(s - i)
      const lit = k > 0 && k < 1 ? smooth(0, 0.25, k) * (1 - smooth(0.85, 1, k)) : 0
      const gone = smooth(0.75, 1, k)
      const a = smooth(0.45, 1, k)
      slab.style.setProperty("--lit", lit.toFixed(3))
      slab.style.setProperty("--gone", gone.toFixed(3))
      slab.classList.toggle("on", k > 0 && k < 1)
      label.style.setProperty("--gone", gone.toFixed(3))
      label.classList.toggle("on", k > 0 && k < 1)
      tile.style.setProperty("--a", a.toFixed(3))
      tile.classList.toggle("on", k >= 1)
      if (k > 0 && k < 1) {
        // Dots leave the part and flow into the tile.
        ctx.fillStyle = colors[i] ?? ""
        for (let j = 0; j < 22; j++) {
          const t = k * 1.55 - j * 0.03
          if (t <= 0 || t >= 1) continue
          const [x, y] = bez(anchor.a, anchor.b, t)
          const sz = 3 + (j % 3)
          ctx.globalAlpha = 0.35 + 0.65 * (1 - j / 22)
          ctx.fillRect(Math.round(x - sz / 2), Math.round(y - sz / 2), sz, sz)
        }
        ctx.globalAlpha = 1
      }
    }
    const linked = A[hover]
    if (hover >= 0 && linked) {
      // Hover: a quiet dotted link between the pair.
      ctx.fillStyle = colors[hover] ?? ""
      for (let t = 0; t <= 1; t += 0.025) {
        const [x, y] = bez(linked.a, linked.b, t)
        ctx.fillRect(Math.round(x - 1.5), Math.round(y - 1.5), 3, 3)
      }
    }
    for (const c of chores) {
      const at = Number(c.dataset.at)
      const x = smooth(at - 1, at, s)
      c.style.setProperty("--x", x.toFixed(3))
      c.classList.toggle("struck", x >= 1)
    }
    right?.style.setProperty("--end", smooth(N - 0.2, N + 0.8, s).toFixed(3))
  })
}
